import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import moment from 'moment';
import IconChevronDown from 'assets/icon-chevron-down.svg';
import IconChevronUp from 'assets/icon-chevron-up.svg';
import useInlineTruncation from 'hooks/useInlineTruncation';
import Stars from './Stars';
import ExpandedCard from './ExpandedCard';
import {FEEDBACK_REVIEW_STATUS} from './mockData';
import {
  ReviewAvatar,
  ReviewBody,
  ReviewBottomRow,
  ReviewCardOuter,
  ReviewCardWrapper,
  ReviewComment,
  ReviewCommentInlineToggle,
  ReviewCommentToggle,
  ReviewCommentWrapper,
  ReviewDate,
  ReviewIdentity,
  ReviewIdentityText,
  ReviewMethodTag,
  ReviewName,
  ReviewOrderType,
  ReviewStatusBar,
  ReviewStatusBarChevron,
  ReviewSubtitle,
  ReviewTopRow
} from './styles';

const STATUS_LABEL = {
  [FEEDBACK_REVIEW_STATUS.NEEDS_REPLY]: 'Needs Reply',
  [FEEDBACK_REVIEW_STATUS.EMAILED]: 'Replied',
  [FEEDBACK_REVIEW_STATUS.CALLED]: 'Replied',
  [FEEDBACK_REVIEW_STATUS.SKIPPED]: 'Replied'
};

const CONTACT_SOURCE_SAUCE_DASHBOARD = 'sauce_dashboard';

const METHOD_LABEL = {
  [FEEDBACK_REVIEW_STATUS.EMAILED]: 'via email',
  [FEEDBACK_REVIEW_STATUS.CALLED]: 'via phone'
};

const replyLabelFor = (review) => {
  if (review.status === FEEDBACK_REVIEW_STATUS.SKIPPED) return 'Skipped';

  const method = METHOD_LABEL[review.status];
  if (!method) return null;

  const when = review.contactedAt
    ? ` ${moment(review.contactedAt).format('MMM D, YYYY')}`
    : '';
  const source =
    review.status === FEEDBACK_REVIEW_STATUS.EMAILED &&
    review.contactSource === CONTACT_SOURCE_SAUCE_DASHBOARD
      ? ' (Sauce Dashboard)'
      : '';

  return `Replied${when} ${method}${source}`;
};

const initials = (name) => {
  if (!name) return '??';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '??';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
};

const customerSubtitle = (customer) => {
  const type = customer.type === 'repeat' ? 'Repeat customer' : 'New customer';
  const orders = `${customer.orderCount} order${
    customer.orderCount === 1 ? '' : 's'
  }`;
  return `${type} · ${orders}`;
};

const ReviewCard = ({review, isExpanded, onToggle, onContact}) => {
  const sentiment = review.rating <= 3 ? 'negative' : 'positive';
  const isExpandable = review.status === FEEDBACK_REVIEW_STATUS.NEEDS_REPLY;
  const replyLabel = replyLabelFor(review);
  const [showFullComment, setShowFullComment] = useState(false);
  const commentRef = useRef(null);
  const truncatedComment = useInlineTruncation(review.comment, commentRef, {
    enabled: !showFullComment,
    suffixFontWeight: 700
  });

  // Reset to collapsed when switching reviews.
  useEffect(() => {
    setShowFullComment(false);
  }, [review.id]);

  const wrapperProps = isExpandable
    ? {
        role: 'button',
        tabIndex: 0,
        'aria-label': isExpanded ? 'Collapse review' : 'Expand review',
        onClick: () => onToggle(review.id),
        onKeyDown: (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle(review.id);
          }
        }
      }
    : {};

  return (
    <ReviewCardOuter id={`review-${review.id}`}>
      <ReviewCardWrapper $expandable={isExpandable} {...wrapperProps}>
        <ReviewStatusBar $status={review.status}>
          <span>{STATUS_LABEL[review.status]}</span>
          {isExpandable && (
            <ReviewStatusBarChevron>
              <img src={isExpanded ? IconChevronUp : IconChevronDown} alt='' />
            </ReviewStatusBarChevron>
          )}
        </ReviewStatusBar>
        <ReviewBody>
          <ReviewTopRow>
            <ReviewIdentity>
              <ReviewAvatar $sentiment={sentiment}>
                {initials(review.customerName)}
              </ReviewAvatar>
              <ReviewIdentityText>
                <ReviewName>{review.customerName}</ReviewName>
                {!isExpanded && review.customer && (
                  <ReviewSubtitle>
                    {customerSubtitle(review.customer)}
                  </ReviewSubtitle>
                )}
              </ReviewIdentityText>
            </ReviewIdentity>
            <ReviewOrderType>{review.orderType}</ReviewOrderType>
          </ReviewTopRow>
          <Stars value={review.rating} sentiment={sentiment} />
          <ReviewCommentWrapper>
            <ReviewComment ref={commentRef}>
              {truncatedComment !== null && !showFullComment ? (
                <>
                  {truncatedComment}
                  {'... '}
                  <ReviewCommentInlineToggle
                    type='button'
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowFullComment(true);
                    }}
                  >
                    View All
                  </ReviewCommentInlineToggle>
                </>
              ) : (
                review.comment
              )}
            </ReviewComment>
            {showFullComment && (
              <ReviewCommentToggle
                type='button'
                onClick={(e) => {
                  e.stopPropagation();
                  setShowFullComment(false);
                }}
              >
                View Less
              </ReviewCommentToggle>
            )}
          </ReviewCommentWrapper>
          <ReviewBottomRow>
            <ReviewDate>
              Review received {moment(review.createdAt).format('MMM D, YYYY')}
            </ReviewDate>
            {replyLabel && <ReviewMethodTag>{replyLabel}</ReviewMethodTag>}
          </ReviewBottomRow>
        </ReviewBody>
      </ReviewCardWrapper>
      {isExpandable && isExpanded && (
        <ExpandedCard review={review} onContact={onContact} />
      )}
    </ReviewCardOuter>
  );
};

ReviewCard.propTypes = {
  review: PropTypes.object.isRequired,
  isExpanded: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  onContact: PropTypes.func.isRequired
};

ReviewCard.defaultProps = {
  isExpanded: false
};

export default ReviewCard;
